import { useMemo } from 'react';

import Grid from '~/common/mui/Grids/Grid';
import GridItem from '~/common/mui/Grids/GridItem';

import Heading from '../../components/Heading';
import ConditionEvaluator from '../../components/utilityComponents/ConditionEvaluator';
import QuestionResolutionWrapper from '../../components/utilityComponents/QuestionResolutionWrapper';
import CheckboxController from '../../controllers/CheckboxController';
import InputController from '../../controllers/InputController';
import ComponentMapper from '../../mappers/ComponentMapper';
import {
  useQuestionLibraryActions,
  useQuestionLibraryFunctions,
  useQuestionLibraryStore,
} from '../../store/questionLibraryStore';
import { SMART_ANSWER_LABEL_TYPES } from '../../utils/constants';
import { useConditionsEvaluator } from '../../utils/useConditionsEvaluator';
import { formatFieldName } from '../../utils/utils';

type RenderFieldProps = {
  field: any;
  isVisible: boolean;
  isParentShowing: boolean;
  metadata: Record<string, any>;
  setFocusedQuestion: (name: string, smartAnswer?: any) => void;
};

function RenderField({
  field,
  isVisible,
  isParentShowing,
  metadata,
  setFocusedQuestion,
}: Readonly<RenderFieldProps>) {
  const { isRequired } = useConditionsEvaluator(field.conditions);

  if (field.componentType === 'component') {
    return <ComponentMapper component={field} />;
  }
  if (field.type === 'checkbox') {
    return (
      <CheckboxController
        question={field}
        padding={field.padding ?? '2rem 0'}
      />
    );
  }
  return (
    <InputController
      question={field}
      isParentShowing={isVisible && isParentShowing}
      showIsRequired={true}
      isRequirable={isRequired}
      padding={'0 0 1.25rem 0'}
      maxInputWidth={'100%'}
      metadata={metadata}
      smartAnswerLayout={SMART_ANSWER_LABEL_TYPES.SNAPSHOT_BUTTON_AND_ICON}
      onFocus={() => setFocusedQuestion(field.name, field.smartAnswer)}
    />
  );
}

type QuestionLibraryGridProps = {
  question: any;
  isParentShowing?: boolean;
};

/**
 * @typedef {object} QuestionLibraryGridProps
 * @property {import('./../../types/complexQuestion').FormGrid} question
 * @param {QuestionLibraryGridProps} question
 * @returns {JSX.Element}
 */
export default function QuestionLibraryGrid({
  question,
  isParentShowing = true,
}: Readonly<QuestionLibraryGridProps>) {
  const {
    children,
    columns,
    columnSizing,
    gap = '0.75rem 1.5rem',
    contentPadding,
    title,
    name = 'grid',
    resolutions = [],
    placeholders = [],
    hasBottomBorder = true,
    metadata = {},
  } = question;
  const { setFocusedQuestion } = useQuestionLibraryActions();
  const { getSmartAnswer } = useQuestionLibraryFunctions();
  const isAiHiddenByQuestion = useQuestionLibraryStore(
    (s) => s.screenData?.isAiHiddenByQuestion ?? {},
  );

  const { isVisible } = useConditionsEvaluator(question.conditions, metadata);

  const fields = useMemo(() => {
    return children.map((field) => {
      field.smartAnswer = getSmartAnswer(formatFieldName(field.name));
      return field;
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [children, name]);

  return (
    <QuestionResolutionWrapper
      isVisible={isVisible}
      resolutions={resolutions}
      hasBottomBorder={hasBottomBorder}
      padding={contentPadding}
      testId={name}
    >
      {title && (
        <Heading
          text={title}
          id={`grid-heading-for-${name}`}
          testId={`grid-heading-for-${name}`}
          placeholders={placeholders}
        />
      )}
      <Grid
        gridTemplateColumns={columnSizing ?? `repeat(${columns}, 1fr)`}
        gap={gap}
        id={name}
        data-testId={`grid-for-${name}`}
        alignItems={'flex-start'}
      >
        {fields.map((field) => (
          <ConditionEvaluator
            conditions={field.conditions}
            conditionResult={!isAiHiddenByQuestion?.[field.name]}
            key={field.name}
            metadata={metadata}
          >
            <GridItem
              gridColumn={`span ${field.span ?? 1}`}
              colStart={field.colStart ?? undefined}
              data-testId={`field-${field.name}`}
            >
              <RenderField
                field={field}
                isVisible={isVisible}
                isParentShowing={isParentShowing}
                metadata={metadata}
                setFocusedQuestion={setFocusedQuestion}
              />
            </GridItem>
          </ConditionEvaluator>
        ))}
      </Grid>
    </QuestionResolutionWrapper>
  );
}
